import React, { useState, useEffect, useRef, useCallback } from 'react';
import { avisar } from '../components/confirmacao';
import { useSearchParams, useNavigate } from 'react-router-dom';
import apiService from '../services/apiService';
import Loading from '../components/Loading';
import { extractSubdomain } from '../utils/subdomain';
import './FeedbacksManager.css';

// Ordena feedbacks priorizando:
// 1) likes === -1 e sem manager_comments
// 2) likes === -1 (dislike, já com manager_comments)
// 3) demais, ordenados por final_score / nota_geral (asc, piores primeiro)
const sortFeedbacksForManager = (list) => {
    const groupOf = (f) => {
        const isDislike = Number(f.likes) === -1;
        const hasManagerComments = Array.isArray(f.manager_comments) && f.manager_comments.length > 0;
        if (isDislike && !hasManagerComments) return 0;
        if (isDislike) return 1;
        return 2;
    };
    const scoreOf = (f) => {
        const v = f.final_score ?? f.nota_geral;
        const n = Number(v);
        return Number.isFinite(n) ? n : Number.POSITIVE_INFINITY;
    };
    const dateOf = (f) => new Date(f.created_at || f.date || 0).getTime();

    return [...list].sort((a, b) => {
        const ga = groupOf(a);
        const gb = groupOf(b);
        if (ga !== gb) return ga - gb;
        const sa = scoreOf(a);
        const sb = scoreOf(b);
        if (sa !== sb) return sa - sb;
        const da = dateOf(a);
        const db = dateOf(b);
        if (db !== da) return db - da;
        return (b.id || 0) - (a.id || 0);
    });
};

const FeedbacksManager = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();

    const [feedbacks, setFeedbacks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [playingAudios, setPlayingAudios] = useState({});
    const [loadingAudios, setLoadingAudios] = useState({});
    const [responses, setResponses] = useState({});
    const [savingResponses, setSavingResponses] = useState({});
    const [, setSavedResponses] = useState({}); // Controla quais respostas foram salvas
    const [filterType, setFilterType] = useState('todos');
    const [dateSortOrder, setDateSortOrder] = useState(null); // null | 'desc' | 'asc'
    // TODO: integrar com backend para persistir avaliação e comentário do gestor
    const [managerLikes, setManagerLikes] = useState({}); // { [feedbackId]: 1 | 0 | null }
    const [managerComments, setManagerComments] = useState({});
    const [savingManager, setSavingManager] = useState({});
    const [savedManager, setSavedManager] = useState({});
    const [managerCommentIds, setManagerCommentIds] = useState({}); // { [feedbackId]: manager_comment.id }
    const [userRole, setUserRole] = useState(null);
    const [storeDisplayMap, setStoreDisplayMap] = useState({}); // { [store_name]: display_name }
    const audioRefs = useRef({});
    const highlightedRowRef = useRef(null);

    // Parâmetros da URL
    const salesman = searchParams.get('salesman');
    const storeName = searchParams.get('store');
    const fullname = searchParams.get('fullname');
    const bucketName = searchParams.get('bucket_name');
    const feedbackIdParam = searchParams.get('feedback_id'); // legado (id único)
    const feedbackIdsParam = searchParams.get('feedback_ids'); // lista separada por vírgula
    const feedbackIdSet = feedbackIdsParam
        ? new Set(feedbackIdsParam.split(',').map(id => id.trim()))
        : feedbackIdParam
            ? new Set([String(feedbackIdParam)])
            : null;

    const loadFeedbacks = useCallback(async () => {
        try {
            setLoading(true);
            setError('');

            // Calcular datas - hoje e 60 dias atrás
            const today = new Date();
            const endDate = today.toISOString().split('T')[0]; // YYYY-MM-DD

            const startDate = new Date();
            startDate.setDate(startDate.getDate() - 60);
            const startDateStr = startDate.toISOString().split('T')[0]; // YYYY-MM-DD

            // Obter user_id do usuário autenticado
            const userInfo = await apiService.getUserInfo();
            const fetchedUserId = userInfo.user.user_id;
            setUserRole(userInfo.user.role || null);
            // Map de store_name -> display_name
            const sdMap = {};
            (userInfo.user.stores || []).forEach(s => {
                sdMap[s.store_name] = s.store_display_name || s.display_name || s.store_name;
            });
            setStoreDisplayMap(sdMap);

            const requestBody = {
                user_id: fetchedUserId,
                salesman: salesman,
                start: startDateStr,
                end: endDate,
                store_name: storeName,
                show_all: true
            };

            // Se temos feedback_ids (vindos de alertas), enviar para a API para busca direta
            if (feedbackIdsParam) {
                requestBody.feedback_ids = [...new Set(feedbackIdsParam.split(',').map(id => id.trim()))].map(Number);
            }
            if (bucketName) {
                requestBody.bucket_name = bucketName;
            }

            const response = await apiService.getFeedbacks(requestBody);

            if (response.success) {
                const list = (response.data.feedbacks || []).map(f => ({
                    ...f,
                    _storeName: f.store_name || storeName,
                }));
                const sorted = sortFeedbacksForManager(list);
                setFeedbacks(sorted);

                // Carregar respostas existentes do campo comments_answer
                const existingResponses = {};
                const existingSavedResponses = {};
                const mLikes = {};
                const mComments = {};
                const mIds = {};
                const mSaved = {};
                list.forEach(feedback => {
                    if (feedback.comments_answer && feedback.comments_answer.trim() !== '') {
                        existingResponses[feedback.id] = feedback.comments_answer;
                        existingSavedResponses[feedback.id] = true;
                    }
                    const mc = (feedback.manager_comments || []).find(m => m.user_id === fetchedUserId);
                    if (mc) {
                        mIds[feedback.id] = mc.id;
                        if (mc.likes !== null && mc.likes !== undefined) mLikes[feedback.id] = mc.likes;
                        if (mc.comments) mComments[feedback.id] = mc.comments;
                        mSaved[feedback.id] = true;
                    }
                });
                setResponses(existingResponses);
                setSavedResponses(existingSavedResponses);
                setManagerLikes(mLikes);
                setManagerComments(mComments);
                setManagerCommentIds(mIds);
                setSavedManager(mSaved);
            } else {
                setError(response.message || 'Erro ao carregar feedbacks');
            }

        } catch (error) {
            console.error('❌ Erro ao carregar feedbacks:', error);
            setError('Erro ao carregar feedbacks: ' + (error.message || 'Erro desconhecido'));
        } finally {
            setLoading(false);
        }
    }, [salesman, storeName, feedbackIdsParam, bucketName]);

    // Carrega feedbacks de TODOS os vendedores de TODAS as lojas com permissão
    // (modo agregado, quando a página é aberta pelo menu sem filtros)
    const loadAllFeedbacks = useCallback(async () => {
        try {
            setLoading(true);
            setError('');

            const today = new Date();
            const endDate = today.toISOString().split('T')[0];
            const startD = new Date();
            startD.setDate(startD.getDate() - 60);
            const startDateStr = startD.toISOString().split('T')[0];

            // 1) Usuário autenticado + lojas permitidas (vem do /auth)
            const userInfo = await apiService.getUserInfo();
            const u = userInfo.user;
            setUserRole(u.role || null);
            // Map de store_name -> display_name
            const sdMap = {};
            (u.stores || []).forEach(s => {
                sdMap[s.store_name] = s.store_display_name || s.display_name || s.store_name;
            });
            setStoreDisplayMap(sdMap);

            const stores = u.stores || [];
            if (stores.length === 0) {
                setFeedbacks([]);
                return;
            }

            // Resolver subdomain (igual ao Home.js)
            const urlSub = extractSubdomain();
            const subdomain = (urlSub && urlSub !== 'admin')
                ? urlSub
                : (stores[0]?.subdomain || stores[0]?.bucket_name || 'demo');

            // 2) Buscar dashdata para descobrir os vendedores existentes nas lojas permitidas
            const storeIds = stores.map(s => s.store_id);
            const dash = await apiService.getDashData({
                subdomains: [subdomain],
                store_ids: storeIds,
                salesman_ids: [],
                date_start: startDateStr,
                date_end: endDate,
            });

            const company = dash?.data?.[0];
            const bySalesman = (company?.by_salesman || []).filter(s => s.is_visible !== false);

            // 3) Deduplicar pares (salesman, store_name) — se houver filtro de loja, restringe a ela
            const seen = new Set();
            const pairs = [];
            bySalesman.forEach(s => {
                const salesmanKey = s.username || s.salesman_name;
                const storeKey = s.store_name;
                if (!salesmanKey || !storeKey) return;
                if (storeName && storeKey !== storeName) return;
                const k = `${storeKey}::${salesmanKey}`;
                if (seen.has(k)) return;
                seen.add(k);
                pairs.push({ salesman: salesmanKey, store_name: storeKey });
            });

            if (pairs.length === 0) {
                setFeedbacks([]);
                return;
            }

            // 4) Buscar feedbacks em paralelo (com limite de concorrência)
            const CONCURRENCY = 6;
            const all = [];
            const existingResp = {};
            const existingSaved = {};
            const mLikes = {};
            const mComments = {};
            const mIds = {};
            const mSaved = {};

            let cursor = 0;
            const worker = async () => {
                while (cursor < pairs.length) {
                    const i = cursor++;
                    const { salesman: sName, store_name: stName } = pairs[i];
                    try {
                        const r = await apiService.getFeedbacks({
                            user_id: u.user_id,
                            salesman: sName,
                            start: startDateStr,
                            end: endDate,
                            store_name: stName,
                            show_all: true,
                        });
                        if (r?.success && r.data?.feedbacks) {
                            r.data.feedbacks.forEach(f => {
                                all.push({ ...f, _storeName: f.store_name || stName });
                                if (f.comments_answer && f.comments_answer.trim() !== '') {
                                    existingResp[f.id] = f.comments_answer;
                                    existingSaved[f.id] = true;
                                }
                                const mc = (f.manager_comments || []).find(m => m.user_id === u.user_id);
                                if (mc) {
                                    mIds[f.id] = mc.id;
                                    if (mc.likes !== null && mc.likes !== undefined) mLikes[f.id] = mc.likes;
                                    if (mc.comments) mComments[f.id] = mc.comments;
                                    mSaved[f.id] = true;
                                }
                            });
                        }
                    } catch (err) {
                        console.warn('Falha ao buscar feedbacks de', sName, stName, err);
                    }
                }
            };
            await Promise.all(
                Array.from({ length: Math.min(CONCURRENCY, pairs.length) }, worker)
            );

            // Ordenação prioritária do gestor
            const sortedAll = sortFeedbacksForManager(all);

            setFeedbacks(sortedAll);
            setResponses(existingResp);
            setSavedResponses(existingSaved);
            setManagerLikes(mLikes);
            setManagerComments(mComments);
            setManagerCommentIds(mIds);
            setSavedManager(mSaved);
        } catch (err) {
            console.error('❌ Erro ao carregar feedbacks (todos):', err);
            setError('Erro ao carregar feedbacks: ' + (err.message || 'Erro desconhecido'));
        } finally {
            setLoading(false);
        }
    }, [storeName]);

    // Scroll para o primeiro feedback destacado quando os dados carregarem
    useEffect(() => {
        if (!loading && feedbackIdSet && highlightedRowRef.current) {
            highlightedRowRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    }, [loading, feedbacks]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!salesman) {
            // Sem vendedor: carregar TODOS os feedbacks (filtrando por loja se vier `store`)
            loadAllFeedbacks();
        } else {
            loadFeedbacks();
        }

        // Cleanup function para pausar todos os áudios quando o componente for desmontado
        const currentAudioRefs = audioRefs.current;
        return () => {
            Object.values(currentAudioRefs).forEach(audio => {
                if (audio && !audio.paused) {
                    audio.pause();
                }
            });
        };
    }, [salesman, storeName, loadFeedbacks, loadAllFeedbacks]);

    // A API entrega o horário do feedback já em hora do Brasil (start_gmt vem
    // direto do relógio do dispositivo). Formatamos a string literalmente, sem
    // new Date()/toLocale, para não sofrer reconversão pelo fuso do navegador.
    const matchDateParts = (dateString) =>
        dateString ? String(dateString).match(/(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/) : null;

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const m = matchDateParts(dateString);
        if (!m) return String(dateString);
        return `${m[3]}/${m[2]}/${m[1]} ${m[4]}:${m[5]}`;
    };

    const formatDateParts = (dateString) => {
        if (!dateString) return { date: 'N/A', time: '' };
        const m = matchDateParts(dateString);
        if (!m) return { date: String(dateString), time: '' };
        return {
            date: `${m[3]}/${m[2]}/${m[1]}`,
            time: `${m[4]}:${m[5]}`,
        };
    };

    const formatAudioLength = (seconds) => {
        if (!seconds || seconds <= 0) return 'N/A';

        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = Math.floor(seconds % 60);
        return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
    };

    // O feedback vem da API como string (formato antigo) OU objeto {good, bad} (formato novo do n8n).
    // Renderizar um objeto direto como filho React quebra a tela inteira. Este helper trata os dois casos.
    const renderFeedbackText = (fb) => {
        if (fb && typeof fb === 'object' && !Array.isArray(fb)) {
            const { good, bad } = fb;
            if (good == null && bad == null) return JSON.stringify(fb);
            return (
                <>
                    {good && (
                        <div className="feedback-good">
                            <strong>✅ Pontos positivos</strong>
                            <div style={{ whiteSpace: 'pre-line' }}>{good}</div>
                        </div>
                    )}
                    {bad && (
                        <div className="feedback-bad" style={{ marginTop: good ? 8 : 0 }}>
                            <strong>⚠️ Pontos de atenção</strong>
                            <div style={{ whiteSpace: 'pre-line' }}>{bad}</div>
                        </div>
                    )}
                </>
            );
        }
        return fb;
    };

    const getReadIcon = (read) => {
        if (read === 1) {
            return <span className="icon read" title="Lido">✅</span>;
        } else {
            return <span className="icon unread" title="Não lido">🙈</span>;
        }
    };

    const getLikesIcon = (likes) => {
        if (likes === null || likes === undefined) {
            return <span className="icon no-reaction" title="Sem reação">-</span>;
        } else if (likes === 1) {
            return <span className="icon thumbs-up" title="Curtiu">👍</span>;
        } else {
            return <span className="icon thumbs-down" title="Não curtiu">👎</span>;
        }
    };

    const handleBackClick = () => {
        const scrollY = searchParams.get('scrollY');
        if (scrollY) {
            navigate(`/?scrollY=${scrollY}`);
        } else {
            navigate(-1);
        }
    };

    const handleResponseChange = (feedbackId, value) => {
        setResponses(prev => ({ ...prev, [feedbackId]: value }));
    };

    // ===== Gestor: like/dislike + comentário =====
    // Convenção: like = 1, dislike = -1, vazio = 0
    const handleManagerLike = (feedbackId, value) => {
        const current = managerLikes[feedbackId];
        const newValue = current === value ? 0 : value;
        setManagerLikes(prev => ({ ...prev, [feedbackId]: newValue }));
        setSavedManager(prev => ({ ...prev, [feedbackId]: false }));
        // Auto-save imediato para criar/atualizar o registro
        handleSaveManager(feedbackId, { likes: newValue });
    };

    const handleManagerCommentChange = (feedbackId, value) => {
        setManagerComments(prev => ({ ...prev, [feedbackId]: value }));
        setSavedManager(prev => ({ ...prev, [feedbackId]: false }));
    };

    const handleSaveManager = async (feedbackId, overrides = {}) => {
        try {
            setSavingManager(prev => ({ ...prev, [feedbackId]: true }));

            const subdomain = (() => {
                const sub = extractSubdomain();
                return sub && sub !== 'admin' ? sub : undefined;
            })();

            const likes = overrides.likes !== undefined ? overrides.likes : managerLikes[feedbackId];
            const comments = overrides.comments !== undefined ? overrides.comments : (managerComments[feedbackId] ?? '');
            const existingId = managerCommentIds[feedbackId];

            const payload = {
                feedback_id: feedbackId,
                comments,
                likes: (likes === undefined || likes === null) ? 0 : likes,
                subdomain,
            };

            const result = existingId
                ? await apiService.updateManagerComment({ id: existingId, ...payload })
                : await apiService.createManagerComment(payload);

            if (!result.success) {
                throw new Error(result.message || 'Erro ao salvar avaliação do gestor');
            }

            // Se foi um POST e a API retornou um id, guardar para próximas edições virarem PUT
            // Estrutura: { success, data: { success, data: { message, subdomain, data: { id, ... } } } }
            const newId = result.data?.data?.data?.id
                || result.data?.data?.id
                || result.data?.id;
            if (!existingId && newId) {
                setManagerCommentIds(prev => ({ ...prev, [feedbackId]: newId }));
            }

            setSavedManager(prev => ({ ...prev, [feedbackId]: true }));
        } catch (err) {
            console.error('❌ Erro ao salvar avaliação do gestor:', err);
            avisar('Erro ao salvar avaliação do gestor: ' + (err.message || ''), { titulo: 'Erro ao salvar' });
        } finally {
            setSavingManager(prev => ({ ...prev, [feedbackId]: false }));
        }
    };

    const handleSaveResponse = async (feedbackId) => {
        const responseText = responses[feedbackId];
        if (!responseText || responseText.trim() === '') {
            return;
        }

        try {
            setSavingResponses(prev => ({ ...prev, [feedbackId]: true }));

            const result = await apiService.saveFeedbackResponse(feedbackId, responseText);

            if (result.success) {
                setSavedResponses(prev => ({ ...prev, [feedbackId]: true }));
            } else {
                throw new Error(result.message || 'Erro ao salvar resposta');
            }

        } catch (error) {
            console.error('❌ Erro ao salvar resposta:', error);
            avisar('Erro ao salvar resposta: ' + error.message, { titulo: 'Erro ao salvar' });
        } finally {
            setSavingResponses(prev => ({ ...prev, [feedbackId]: false }));
        }
    };

    // Função para filtrar feedbacks
    const getFilteredFeedbacks = () => {
        let list;
        // Filtro por feedback_ids (prioridade máxima — vem da URL)
        if (feedbackIdSet) {
            list = feedbacks.filter(feedback => feedbackIdSet.has(String(feedback.id)));
        } else if (filterType === 'pendentes') {
            // Mesmo critério do grupo 0 da ordenação padrão (sortFeedbacksForManager):
            // dislikes (likes === -1) sem manager_comments
            list = feedbacks.filter(feedback => {
                const isDislike = Number(feedback.likes) === -1;
                const hasManagerComments = Array.isArray(feedback.manager_comments) && feedback.manager_comments.length > 0;
                return isDislike && !hasManagerComments;
            });
        } else {
            list = feedbacks;
        }

        if (dateSortOrder) {
            const dateOf = (f) => new Date(f.start_gmt || f.start || f.created_at || f.date || 0).getTime();
            list = [...list].sort((a, b) => {
                const da = dateOf(a);
                const db = dateOf(b);
                return dateSortOrder === 'asc' ? da - db : db - da;
            });
        }

        return list;
    };

    const handleAudioPlay = async (audioCommentId, feedbackDate, bucketName) => {
        try {
            // Extrair apenas o ID numérico se vier no formato "data__id"
            const actualId = audioCommentId.toString().includes('__')
                ? audioCommentId.toString().split('__').pop()
                : audioCommentId;

            setLoadingAudios(prev => ({ ...prev, [audioCommentId]: true }));

            // Se já está tocando, pausar
            if (playingAudios[audioCommentId]) {
                const audio = audioRefs.current[audioCommentId];
                if (audio) {
                    audio.pause();
                    setPlayingAudios(prev => ({ ...prev, [audioCommentId]: false }));
                }
                return;
            }

            // Pausar todos os outros áudios
            Object.keys(audioRefs.current).forEach(id => {
                const audio = audioRefs.current[id];
                if (audio && !audio.paused) {
                    audio.pause();
                    setPlayingAudios(prev => ({ ...prev, [id]: false }));
                }
            });

            // Criar ou reutilizar elemento de áudio
            let audio = audioRefs.current[audioCommentId];
            if (!audio) {
                audio = new Audio();
                audioRefs.current[audioCommentId] = audio;

                // Event listeners
                audio.addEventListener('ended', () => {
                    setPlayingAudios(prev => ({ ...prev, [audioCommentId]: false }));
                });

                audio.addEventListener('error', (e) => {
                    console.error('❌ Erro no áudio:', e);
                    setPlayingAudios(prev => ({ ...prev, [audioCommentId]: false }));
                    setLoadingAudios(prev => ({ ...prev, [audioCommentId]: false }));
                });

                audio.addEventListener('loadstart', () => {
                });

                audio.addEventListener('canplay', () => {
                    setLoadingAudios(prev => ({ ...prev, [audioCommentId]: false }));
                });
            }

            // Usar URL do proxy em vez da URL direta do S3, passando bucket_name do feedback
            const proxyUrl = `/api/audio-proxy/${actualId}${bucketName ? `?bucket_name=${encodeURIComponent(bucketName)}` : ''}`;

            audio.src = proxyUrl;
            await audio.play();
            setPlayingAudios(prev => ({ ...prev, [audioCommentId]: true }));

        } catch (error) {
            console.error('❌ Erro ao reproduzir áudio:', error);
            setError('Erro ao reproduzir áudio: ' + error.message);
        } finally {
            setLoadingAudios(prev => ({ ...prev, [audioCommentId]: false }));
        }
    };

    const renderAudioComments = (audioCommentIds, feedbackDateTime, bucketName) => {
        if (!audioCommentIds || audioCommentIds.length === 0) {
            return <span>-</span>;
        }

        return (
            <div className="audio-comments-list">
                {audioCommentIds.map((audioId, index) => {
                    // Extrair apenas o ID numérico para exibição
                    const displayId = audioId.toString().includes('__')
                        ? audioId.toString().split('__').pop()
                        : audioId;

                    // Texto exibido (data ou data+hora) vindo do próprio audioId ou do feedback
                    const rawText = audioId.toString().includes('__')
                        ? audioId.toString().split('__')[0]
                        : feedbackDateTime;

                    // Quebra em data (linha 1) e hora (linha 2) se houver espaço
                    let displayDate = rawText;
                    let displayTime = '';
                    if (rawText && typeof rawText === 'string') {
                        const parts = rawText.trim().split(/\s+/);
                        if (parts.length >= 2) {
                            displayDate = parts[0];
                            displayTime = parts.slice(1).join(' ');
                        }
                    }

                    return (
                        <div key={audioId} className="audio-comment-item">
                            <div className="audio-comment-info">
                                <div className="audio-comment-line1">
                                    <span className="audio-id">#{displayId}</span>
                                    <span className="audio-date">{displayDate}</span>
                                </div>
                                {displayTime && <div className="audio-time">{displayTime}</div>}
                            </div>
                            <button
                                className={`audio-play-button ${playingAudios[audioId] ? 'playing' : ''}`}
                                onClick={() => handleAudioPlay(audioId, feedbackDateTime, bucketName)}
                                disabled={loadingAudios[audioId]}
                                title={playingAudios[audioId] ? 'Pausar áudio' : 'Reproduzir áudio'}
                            >
                                {loadingAudios[audioId] ? (
                                    <span className="loading-spinner">⏳</span>
                                ) : playingAudios[audioId] ? (
                                    <span className="pause-icon">⏸️</span>
                                ) : (
                                    <span className="play-icon">▶️</span>
                                )}
                            </button>
                        </div>
                    );
                })}
            </div>
        );
    };

    if (loading) {
        return <Loading message="Carregando feedbacks..." fullScreen />;
    }

    if (error) {
        return (
            <div className="error-container">
                <h2>Erro</h2>
                <p>{error}</p>
                <button onClick={handleBackClick} className="back-button">
                    Voltar
                </button>
            </div>
        );
    }

    return (
        <div className="feedbacks-manager home-root">
            <div className="feedbacks-header">
                <button onClick={handleBackClick} className="back-button">
                    ← Voltar
                </button>
                <div className="salesman-info">
                    <h1>
                        Feedbacks{salesman ? ' ' : ''}
                        {(fullname || salesman) && (
                            <span className="salesman-highlight">{fullname || salesman}</span>
                        )}
                    </h1>
                    {storeName && (
                        <p className="store-info">Loja: {storeName.replace(/_/g, ' ')}</p>
                    )}
                    <p className="total-info">Total de feedbacks: {getFilteredFeedbacks().length} {feedbackIdSet ? `(filtrado por ${feedbackIdSet.size} ID${feedbackIdSet.size !== 1 ? 's' : ''})` : filterType === 'pendentes' ? '(pendentes de resposta)' : '(últimos 60 dias)'}</p>
                </div>
                <div className="filter-section">
                    {feedbackIdSet ? (
                        <button
                            className="filter-clear-button"
                            onClick={() => navigate(`/feedbacks?salesman=${encodeURIComponent(salesman)}&store=${encodeURIComponent(storeName)}`)}
                        >
                            ✕ Remover filtro de ID
                        </button>
                    ) : (
                        <>
                            <label className="filter-label">Filtrar por:</label>
                            <select
                                className="filter-select"
                                value={filterType}
                                onChange={(e) => setFilterType(e.target.value)}
                            >
                                <option value="todos">Todos</option>
                                <option value="pendentes">Pendentes de resposta</option>
                            </select>
                        </>
                    )}
                </div>
            </div>

            {getFilteredFeedbacks().length === 0 ? (
                <div className="no-feedbacks">
                    <p>
                        {feedbackIdSet
                            ? feedbacks.length > 0
                                ? `Os IDs buscados (${[...feedbackIdSet].join(', ')}) não foram encontrados nos ${feedbacks.length} feedbacks carregados.`
                                : salesman
                                    ? `Nenhum feedback encontrado para este vendedor/loja. Verifique se o vendedor "${salesman}" e a loja "${storeName}" estão corretos.`
                                    : `Nenhum feedback encontrado para a loja "${storeName}".`
                            : filterType === 'pendentes'
                                ? 'Nenhum feedback pendente de resposta encontrado.'
                                : salesman
                                    ? 'Nenhum feedback encontrado para este vendedor nos últimos 60 dias.'
                                    : storeName
                                        ? `Nenhum feedback encontrado para a loja "${storeName}" nos últimos 60 dias.`
                                        : 'Nenhum feedback encontrado nos últimos 60 dias.'}
                    </p>
                    {feedbackIdSet && feedbacks.length > 0 && (
                        <button
                            className="filter-clear-button"
                            style={{ marginTop: '12px' }}
                            onClick={() => navigate(`/feedbacks?salesman=${encodeURIComponent(salesman)}&store=${encodeURIComponent(storeName)}${fullname ? `&fullname=${encodeURIComponent(fullname)}` : ''}`)}
                        >
                            Ver todos os feedbacks deste vendedor
                        </button>
                    )}
                </div>
            ) : (
                <div className="feedbacks-table-container">
                    <table className="feedbacks-table">
                        <thead>
                            <tr className="feedbacks-group-row">
                                <th colSpan={userRole === 'manager' ? 7 : 6}></th>
                                <th colSpan={5} className="group-vendedor">Vendedor</th>
                                <th colSpan={1} className="group-gestor">Gestor</th>
                            </tr>
                            <tr>
                                <th>ID (feedback_id)</th>
                                <th
                                    className="sortable-header"
                                    onClick={() => setDateSortOrder(prev => prev === 'desc' ? 'asc' : 'desc')}
                                    title="Clique para ordenar por data"
                                    style={{ cursor: 'pointer', userSelect: 'none' }}
                                >
                                    Data/Hora{' '}
                                    <span className="sort-icon" style={{ opacity: dateSortOrder ? 1 : 0.4 }}>
                                        {dateSortOrder === 'asc' ? '▲' : dateSortOrder === 'desc' ? '▼' : '⇅'}
                                    </span>
                                </th>
                                {userRole === 'manager' && <th>Loja / Depto</th>}
                                <th>Duração do Áudio</th>
                                <th>Duração da Transcrição</th>
                                <th>Nota</th>
                                <th>Resumo</th>
                                <th>Feedback</th>
                                <th>Lido</th>
                                <th>Likes</th>
                                <th>Comentários</th>
                                <th>Áudios</th>
                                <th>Avaliação &amp; Comentário</th>
                            </tr>
                        </thead>
                        <tbody>
                            {getFilteredFeedbacks().map((feedback, index) => {
                                const isHighlighted = feedbackIdSet?.has(String(feedback.id));
                                return (
                                    <tr key={feedback.id} ref={isHighlighted && index === 0 ? highlightedRowRef : null} className={`feedback-row${isHighlighted ? ' feedback-row-highlighted' : ''}`}>
                                        <td className="feedback-id">{feedback.id}</td>
                                        <td className="feedback-date">
                                            {(() => {
                                                const { date, time } = formatDateParts(feedback.start_gmt || feedback.start);
                                                return (
                                                    <>
                                                        <div>{date}</div>
                                                        {time && <div className="feedback-time">{time}</div>}
                                                    </>
                                                );
                                            })()}
                                        </td>
                                        {userRole === 'manager' && (
                                            <td className="feedback-store">
                                                {storeDisplayMap[feedback._storeName] || feedback._storeName || '-'}
                                            </td>
                                        )}
                                        <td className="feedback-duration" title="Duração real do áudio gravado">{formatAudioLength(feedback.audio_duration)}</td>
                                        <td className="feedback-duration" title="Até o fim da última fala transcrita">{formatAudioLength(feedback.transcription_length)}</td>
                                        <td className="feedback-rating">{feedback.nota_geral || '-'}</td>
                                        <td className="feedback-summary">
                                            <div className="feedback-content">
                                                {feedback.summary || '-'}
                                            </div>
                                        </td>
                                        <td className="feedback-text">
                                            <div className="feedback-content">
                                                {renderFeedbackText(feedback.feedback)}
                                            </div>
                                        </td>
                                        <td className="feedback-read">{getReadIcon(feedback.read)}</td>
                                        <td className="feedback-likes">{getLikesIcon(feedback.likes)}</td>
                                        <td className="feedback-comments">
                                            <div className="comments-section">
                                                <div className="feedback-content">
                                                    {feedback.comments || '-'}
                                                </div>
                                                <div className="response-section">
                                                    <textarea
                                                        className="response-textarea"
                                                        placeholder="Digite sua resposta..."
                                                        value={responses[feedback.id] || ''}
                                                        onChange={(e) => handleResponseChange(feedback.id, e.target.value)}
                                                        rows={2}
                                                    />
                                                    <button
                                                        className="save-response-button"
                                                        onClick={() => handleSaveResponse(feedback.id)}
                                                        disabled={!responses[feedback.id]?.trim() || savingResponses[feedback.id]}
                                                    >
                                                        {savingResponses[feedback.id] ? 'Salvando...' : 'Salvar Resposta'}
                                                    </button>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="feedback-audio-comments">
                                            {renderAudioComments(
                                                feedback.audio_comment_ids,
                                                formatDate(feedback.start_gmt || feedback.start), // data + hora completa
                                                feedback.bucket_name
                                            )}
                                        </td>
                                        <td className="feedback-manager">
                                            <div className="manager-card">
                                                <div className="manager-card-top">
                                                    <button
                                                        type="button"
                                                        className={`manager-like-btn${managerLikes[feedback.id] === 1 ? ' active like' : ''}`}
                                                        onClick={() => handleManagerLike(feedback.id, 1)}
                                                        title="Like"
                                                    >
                                                        👍
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className={`manager-like-btn${managerLikes[feedback.id] === -1 ? ' active dislike' : ''}`}
                                                        onClick={() => handleManagerLike(feedback.id, -1)}
                                                        title="Dislike"
                                                    >
                                                        👎
                                                    </button>
                                                </div>
                                                <div className="manager-card-bottom">
                                                    <textarea
                                                        className="response-textarea"
                                                        placeholder="Comentário do gestor..."
                                                        value={managerComments[feedback.id] || ''}
                                                        onChange={(e) => handleManagerCommentChange(feedback.id, e.target.value)}
                                                        rows={2}
                                                    />
                                                    <button
                                                        type="button"
                                                        className="save-response-button"
                                                        onClick={() => handleSaveManager(feedback.id)}
                                                        disabled={savingManager[feedback.id]}
                                                    >
                                                        {savingManager[feedback.id]
                                                            ? 'Salvando...'
                                                            : savedManager[feedback.id]
                                                                ? '✓ Salvo'
                                                                : 'Salvar'}
                                                    </button>
                                                </div>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default FeedbacksManager;